/**
 * Frontend-Only Sync Service
 * 
 * This service handles all cloud synchronization directly in the frontend,
 * eliminating backend pressure and providing faster, more responsive sync.
 * 
 * Key Benefits:
 * - Zero backend load for Google Drive operations
 * - Faster sync with direct API access
 * - Better offline support with intelligent queuing
 * - Real-time conflict detection and resolution
 * - Automatic retry with exponential backoff
 */

import googleDriveAPI from './googleDriveAPI.js'

class FrontendSyncService {
  constructor() {
    this.isInitialized = false
    this.syncInProgress = false
    this.lastSyncTime = null
    this.conflictResolver = new ConflictResolver()
    this.offlineQueue = new OfflineQueue()
    this.cacheDB = null
    
    // Sync statistics
    this.stats = {
      totalSynced: 0,
      conflicts: 0,
      retries: 0,
      lastSyncDuration: 0
    }

    // Event listeners for network changes
    this.setupNetworkListeners()
  }

  /**
   * Open IndexedDB for caching with multi-device version handling
   */
  async openCacheDB() {
    if (this.cacheDB) return this.cacheDB

    return new Promise((resolve, reject) => {
      // First, try to open without specifying version to get current version
      let openRequest = indexedDB.open('CloudNotesCache')
      
      openRequest.onsuccess = () => {
        const db = openRequest.result
        const currentVersion = db.version
        db.close()
        
        console.log(`🗄️ FrontendSync: Current IndexedDB version: ${currentVersion}`)
        
        // Now open with the current version or higher
        const versionedRequest = indexedDB.open('CloudNotesCache', currentVersion)
        
        versionedRequest.onerror = () => {
          console.error('❌ FrontendSync: IndexedDB open failed:', versionedRequest.error)
          reject(versionedRequest.error)
        }
        
        versionedRequest.onsuccess = () => {
          console.log('✅ FrontendSync: IndexedDB opened successfully')
          const db = versionedRequest.result
          
          // Validate that required object stores exist
          if (!db.objectStoreNames.contains('notes')) {
            console.log('⚠️ FrontendSync: Notes object store missing, need to recreate database')
            db.close()
            
            // Delete and recreate the database with proper schema
            const deleteRequest = indexedDB.deleteDatabase('CloudNotesCache')
            deleteRequest.onsuccess = () => {
              console.log('🗑️ FrontendSync: Deleted corrupted database, recreating...')
              
              // Create fresh database
              const createRequest = indexedDB.open('CloudNotesCache', 1)
              createRequest.onsuccess = () => {
                console.log('✅ FrontendSync: Fresh database created successfully')
                this.cacheDB = createRequest.result
                resolve(this.cacheDB)
              }
              createRequest.onerror = () => reject(createRequest.error)
              createRequest.onupgradeneeded = (event) => {
                const newDb = event.target.result
                const store = newDb.createObjectStore('notes', { keyPath: 'noteId' })
                store.createIndex('lastModified', 'lastModified')
                store.createIndex('folderId', 'folderId')
                console.log('✅ FrontendSync: Fresh notes object store created')
              }
            }
            deleteRequest.onerror = () => reject(deleteRequest.error)
            
            return
          }
          
          this.cacheDB = db
          resolve(this.cacheDB)
        }
        
        versionedRequest.onupgradeneeded = (event) => {
          console.log('🔄 FrontendSync: IndexedDB upgrade needed')
          const db = event.target.result
          if (!db.objectStoreNames.contains('notes')) {
            const store = db.createObjectStore('notes', { keyPath: 'noteId' })
            store.createIndex('lastModified', 'lastModified')
            store.createIndex('folderId', 'folderId')
            console.log('✅ FrontendSync: Created notes object store')
          }
        }
      }
      
      openRequest.onerror = () => {
        console.log('🔄 FrontendSync: No existing database, creating new one')
        // Database doesn't exist, create with version 1
        const createRequest = indexedDB.open('CloudNotesCache', 1)
        
        createRequest.onerror = () => {
          console.error('❌ FrontendSync: Failed to create IndexedDB:', createRequest.error)
          reject(createRequest.error)
        }
        
        createRequest.onsuccess = () => {
          console.log('✅ FrontendSync: New IndexedDB created successfully')
          this.cacheDB = createRequest.result
          resolve(this.cacheDB)
        }
        
        createRequest.onupgradeneeded = (event) => {
          console.log('🔄 FrontendSync: Setting up new IndexedDB schema')
          const db = event.target.result
          const store = db.createObjectStore('notes', { keyPath: 'noteId' })
          store.createIndex('lastModified', 'lastModified')
          store.createIndex('folderId', 'folderId')
          console.log('✅ FrontendSync: New notes object store created')
        }
      }
    })
  }

  /**
   * Initialize the sync service with user's access token
   */
  async initialize(accessToken, userInfo) {
    try {
      // Skip initialization during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping initialization')
        return false
      }

      console.log('🔄 FrontendSync: Initializing with direct Google Drive access...')
      console.log('🔍 FrontendSync: Access token details:', {
        hasToken: !!accessToken,
        tokenLength: accessToken?.length,
        tokenPrefix: accessToken?.substring(0, 20) + '...'
      })
      
      console.log('🔍 FrontendSync: GoogleDriveAPI before initialization:', {
        hasInstance: !!googleDriveAPI,
        accessToken: !!googleDriveAPI?.accessToken,
        initialized: googleDriveAPI?.initialized,
        appFolderId: googleDriveAPI?.appFolderId
      })
      
      await googleDriveAPI.initialize(accessToken)
      
      console.log('🔍 FrontendSync: GoogleDriveAPI after initialization:', {
        hasInstance: !!googleDriveAPI,
        accessToken: !!googleDriveAPI?.accessToken,
        accessTokenLength: googleDriveAPI?.accessToken?.length,
        initialized: googleDriveAPI?.initialized,
        appFolderId: googleDriveAPI?.appFolderId,
        folderCacheSize: googleDriveAPI?.folderCache?.size
      })
      
      // Assign to window for global access (needed by RichTextEditor and other components)
      window.googleDriveAPI = googleDriveAPI
      console.log('🌐 FrontendSync: googleDriveAPI assigned to window for global access')
      
      this.userInfo = userInfo
      this.isInitialized = true
      
      // Process any queued offline operations
      await this.processOfflineQueue()
      
      // Reconcile cloudFileId for existing cached notes
      await this.reconcileAllCachedNotes()
      
      console.log('✅ FrontendSync: Initialized successfully')
      return true
    } catch (error) {
      console.error('❌ FrontendSync: Initialization failed:', error)
      throw error
    }
  }

  /**
   * Sync notes with intelligent batching and conflict detection
   */
  async syncNotes(options = {}) {
    if (!this.isInitialized) {
      throw new Error('Sync service not initialized')
    }

    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping sync')
      return { skipped: true, reason: 'onboarding' }
    }

    if (this.syncInProgress) {
      console.log('🔄 FrontendSync: Sync already in progress, skipping...')
      return { skipped: true }
    }

    this.syncInProgress = true
    const startTime = Date.now()

    try {
      console.log('🔄 FrontendSync: Starting intelligent sync...')

      // Step 1: Get local notes that need syncing
      const localNotes = await this.getLocalNotesForSync()
      
      // Step 2: Get remote changes since last sync
      const remoteChanges = await this.getRemoteChangesSinceLastSync()
      
      // Step 3: Detect and resolve conflicts
      const syncPlan = await this.createSyncPlan(localNotes, remoteChanges)
      
      // Step 4: Execute sync plan
      const result = await this.executeSyncPlan(syncPlan)
      
      // Step 5: Update last sync time
      this.lastSyncTime = new Date()
      localStorage.setItem('lastFrontendSync', this.lastSyncTime.toISOString())
      
      this.stats.lastSyncDuration = Date.now() - startTime
      this.stats.totalSynced += result.synced
      
      console.log(`✅ FrontendSync: Completed in ${this.stats.lastSyncDuration}ms`)
      return result
      
    } catch (error) {
      console.error('❌ FrontendSync: Sync failed:', error)
      throw error
    } finally {
      this.syncInProgress = false
    }
  }

  /**
   * Save note with optimistic sync
   */
  async saveNote(note) {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping note save')
        // Still save locally but don't attempt cloud sync
        const localToPersist = { ...note, needsSync: true }
        this.saveNoteToCacheDB(localToPersist).catch(() => {})
        return { success: true, note, localOnly: true }
      }

      // For frontend-only mode, generate permanent ID if this is a temp note
      let finalNote = { ...note }
      let tempNoteId = null
      if (note.noteId && note.noteId.startsWith('temp-')) {
        tempNoteId = note.noteId // Preserve the temp ID
        const permanentId = `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        finalNote = {
          ...note,
          noteId: permanentId,
          id: permanentId,
          tempNoteId: tempNoteId, // Add temp ID to the note data
          isOptimistic: false,
          uploading: false,
          justUploaded: true
        }
        console.log(`🔄 FrontendSync: Converting temp ID ${note.noteId} to permanent ID ${permanentId}`)
        // Persist a mapping so other views can resolve the final ID from the temp ID
        try {
          const raw = localStorage.getItem('tempToPermMap') || '{}'
          const map = JSON.parse(raw)
          map[note.noteId] = permanentId
          localStorage.setItem('tempToPermMap', JSON.stringify(map))
        } catch (_) {}
      }

      // Always persist locally first with needsSync=true (resilient against auth gaps)
      const localToPersist = { ...finalNote, needsSync: true }
      // Fire-and-forget for large payloads so UI isn't blocked
      this.saveNoteToCacheDB(localToPersist).catch(() => {})

      // Attempt cloud save only if we appear authenticated and initialized
      const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
      const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
      
      console.log('🔍 FrontendSync: Cloud save conditions:', {
        navigatorOnLine: navigator.onLine,
        hasGoogleDriveAPI: !!googleDriveAPI,
        hasToken,
        isInitialized,
        googleDriveAPIState: googleDriveAPI ? {
          accessToken: !!googleDriveAPI.accessToken,
          accessTokenLength: googleDriveAPI.accessToken?.length,
          initialized: googleDriveAPI.initialized,
          appFolderId: googleDriveAPI.appFolderId,
          folderCacheSize: googleDriveAPI.folderCache?.size
        } : 'N/A'
      })
      
      if (navigator.onLine && hasToken && isInitialized) {
        try {
          // Pass the note with temp ID preserved for proper event handling
          await googleDriveAPI.saveNote(finalNote)
          await this.markNoteSynced(finalNote.noteId)
        } catch (cloudError) {
          console.warn('⚠️ FrontendSync: Cloud save failed, queuing for retry:', cloudError?.message || cloudError)
          this.offlineQueue.add('save', finalNote)
        }
      } else {
        // No token/initialized or offline: queue for later
        this.offlineQueue.add('save', finalNote)
        console.log('📱 FrontendSync: Note queued for deferred sync (pending auth or offline)')
      }

      return { success: true, note: finalNote }
    } catch (error) {
      console.error('❌ FrontendSync: Save note failed:', error)
      throw error
    }
  }

  /**
   * Delete note with optimistic sync and attachment cleanup
   */
  async deleteNote(noteId, cloudFileId = null, noteContent = null) {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping note deletion')
        // Still delete locally but don't attempt cloud sync
        await this.deleteNoteLocally(noteId)
        return { success: true, localOnly: true }
      }

      // Immediate local deletion
      await this.deleteNoteLocally(noteId)
      
      // Queue for cloud sync (resilient to auth state)
      const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
      const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
      
      if (navigator.onLine && hasToken && isInitialized) {
        try {
          // GoogleDriveAPI will automatically resolve cloudFileId if not provided
          await googleDriveAPI.deleteNote(noteId, cloudFileId, noteContent)
        } catch (cloudError) {
          console.warn('⚠️ FrontendSync: Cloud delete failed, queuing for retry:', cloudError?.message || cloudError)
          this.offlineQueue.add('delete', { noteId, cloudFileId, noteContent })
        }
      } else {
        this.offlineQueue.add('delete', { noteId, cloudFileId, noteContent })
        console.log('📱 FrontendSync: Note deletion queued for deferred sync (pending auth or offline)')
      }
      
      return { success: true }
    } catch (error) {
      console.error('❌ FrontendSync: Delete note failed:', error)
      throw error
    }
  }

  /**
   * Update a note locally (for ID changes)
   */
  async updateNoteLocally(note) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readwrite')
      const store = transaction.objectStore('notes')
      
      await store.put(note)
      console.log(`💾 FrontendSync: Updated note locally with ID: ${note.noteId}`)
    } catch (error) {
      console.error('❌ FrontendSync: Failed to update note locally:', error)
      throw error
    }
  }

  /**
   * Load notes with intelligent caching
   */
  async loadNotes(options = {}) {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping note loading')
        throw new Error('Note loading not available during onboarding')
      }

      const { useCache = true, forceRefresh = false } = options

      // Return cached notes immediately if available
      if (useCache && !forceRefresh) {
        const cachedNotes = await this.getCachedNotes()
        if (cachedNotes.length > 0) {
          console.log(`📦 FrontendSync: Returning ${cachedNotes.length} cached notes`)
          
          // Load fresh notes in background
          this.loadNotesInBackground()
          
          return { notes: cachedNotes, fromCache: true }
        }
      }

      // Load fresh notes from Google Drive
      const result = await googleDriveAPI.loadNotes(options)
      
      // Cache the results
      await this.cacheNotes(result.notes)
      
      // Filter any non-note records defensively
      const filtered = (result.notes || []).filter(n => typeof n.noteId === 'string' && !n.noteId.startsWith('__folder__:'))
      
      // Reconcile cloudFileId for notes that might be missing it
      await this.reconcileCloudFileIds(filtered)
      
      return { notes: filtered, fromCache: false, hasMore: result.hasMore, nextPageToken: result.nextPageToken }
    } catch (error) {
      console.error('❌ FrontendSync: Load notes failed:', error)
      
      // Fallback to cached notes on error
      const cachedNotes = await this.getCachedNotes()
      if (cachedNotes.length > 0) {
        console.log('⚠️ FrontendSync: Using cached notes as fallback')
        return { notes: cachedNotes, fromCache: true, error }
      }
      
      throw error
    }
  }

  /**
   * Reconcile cloudFileId for notes that might be missing it
   * This ensures all notes have the correct Google Drive file ID
   */
  async reconcileCloudFileIds(notes) {
    try {
      if (!googleDriveAPI || !googleDriveAPI.initialized) {
        console.log('⚠️ FrontendSync: GoogleDriveAPI not ready, skipping cloudFileId reconciliation')
        return
      }

      const notesNeedingReconciliation = notes.filter(note => !note.cloudFileId)
      
      if (notesNeedingReconciliation.length === 0) {
        console.log('✅ FrontendSync: All notes already have cloudFileId')
        return
      }

      console.log(`🔄 FrontendSync: Reconciling cloudFileId for ${notesNeedingReconciliation.length} notes`)
      
      for (const note of notesNeedingReconciliation) {
        try {
          console.log(`🔍 FrontendSync: Looking up cloudFileId for note: ${note.noteId}`)
          const cloudFileId = await googleDriveAPI.getNoteFileId(note.noteId)
          
          if (cloudFileId) {
            console.log(`✅ FrontendSync: Found cloudFileId ${cloudFileId} for note ${note.noteId}`)
            note.cloudFileId = cloudFileId
            // Update the cached version as well
            await this.updateCachedNote(note)
          } else {
            console.log(`⚠️ FrontendSync: No cloudFileId found for note ${note.noteId} - may be local-only`)
          }
        } catch (error) {
          console.warn(`⚠️ FrontendSync: Failed to reconcile cloudFileId for note ${note.noteId}:`, error)
        }
      }
      
      console.log(`✅ FrontendSync: CloudFileId reconciliation completed for ${notesNeedingReconciliation.length} notes`)
    } catch (error) {
      console.error('❌ FrontendSync: CloudFileId reconciliation failed:', error)
    }
  }

  /**
   * Reconcile cloudFileId for all cached notes
   * This is useful for fixing existing notes that are missing cloudFileId
   */
  async reconcileAllCachedNotes() {
    try {
      console.log('🔄 FrontendSync: Starting reconciliation for all cached notes')
      
      const cachedNotes = await this.getCachedNotes()
      if (cachedNotes.length === 0) {
        console.log('📭 FrontendSync: No cached notes to reconcile')
        return
      }
      
      console.log(`🔄 FrontendSync: Found ${cachedNotes.length} cached notes to check`)
      
      // Reconcile cloudFileId for all notes
      await this.reconcileCloudFileIds(cachedNotes)
      
      console.log('✅ FrontendSync: All cached notes reconciliation completed')
    } catch (error) {
      console.error('❌ FrontendSync: Failed to reconcile all cached notes:', error)
    }
  }

  /**
   * Get local notes that need syncing
   */
  async getLocalNotesForSync() {
    const db = await this.openCacheDB()
    const transaction = db.transaction(['notes'], 'readonly')
    const store = transaction.objectStore('notes')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const allNotes = request.result || []
        // Filter notes that have local changes or are new
        const notesToSync = allNotes.filter(note => 
          note.needsSync || 
          note.isOptimistic || 
          !note.cloudFileId ||
          note.locallyModified > note.cloudModified
        )
        resolve(notesToSync)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get remote changes since last sync
   */
  async getRemoteChangesSinceLastSync() {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping remote changes fetch')
      return { notes: [], hasMore: false }
    }

    const lastSync = this.lastSyncTime || new Date(localStorage.getItem('lastFrontendSync') || 0)
    
    try {
      return await googleDriveAPI.loadNotes({
        modifiedAfter: lastSync,
        pageSize: 100 // Larger batch for efficiency
      })
    } catch (error) {
      console.warn('⚠️ FrontendSync: Failed to get remote changes:', error)
      return { notes: [], hasMore: false }
    }
  }

  /**
   * Create intelligent sync plan with conflict detection
   */
  async createSyncPlan(localNotes, remoteChanges) {
    const plan = {
      upload: [],
      download: [],
      conflicts: [],
      delete: []
    }

    // Process local notes for upload
    for (const localNote of localNotes) {
      const remoteNote = remoteChanges.notes.find(r => r.noteId === localNote.noteId)
      
      if (!remoteNote) {
        // New local note or deleted remotely
        plan.upload.push(localNote)
      } else if (await this.hasConflict(localNote, remoteNote)) {
        // Conflict detected
        plan.conflicts.push({ local: localNote, remote: remoteNote })
      } else if (localNote.lastModified > remoteNote.lastModified) {
        // Local is newer
        plan.upload.push(localNote)
      }
    }

    // Process remote notes for download
    for (const remoteNote of remoteChanges.notes) {
      const localNote = localNotes.find(l => l.noteId === remoteNote.noteId)
      
      if (!localNote) {
        // New remote note
        plan.download.push(remoteNote)
      } else if (!plan.conflicts.some(c => c.remote.noteId === remoteNote.noteId) &&
                 remoteNote.lastModified > localNote.lastModified) {
        // Remote is newer and no conflict
        plan.download.push(remoteNote)
      }
    }

    console.log(`📋 FrontendSync: Sync plan - Upload: ${plan.upload.length}, Download: ${plan.download.length}, Conflicts: ${plan.conflicts.length}`)
    
    return plan
  }

  /**
   * Execute sync plan with proper error handling
   */
  async executeSyncPlan(plan) {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping sync plan execution')
      return { synced: 0, conflicts: 0, errors: [], skipped: true, reason: 'onboarding' }
    }

    const result = {
      synced: 0,
      conflicts: plan.conflicts.length,
      errors: []
    }

    // Handle conflicts first
    for (const conflict of plan.conflicts) {
      try {
        const resolution = await this.conflictResolver.resolve(conflict)
        if (resolution.action === 'upload') {
          plan.upload.push(resolution.note)
        } else if (resolution.action === 'download') {
          plan.download.push(resolution.note)
        }
        this.stats.conflicts++
      } catch (error) {
        console.error('❌ FrontendSync: Conflict resolution failed:', error)
        result.errors.push(`Conflict resolution: ${error.message}`)
      }
    }

    // Execute uploads with batching
    for (const note of plan.upload) {
      try {
        await googleDriveAPI.saveNote(note)
        await this.markNoteSynced(note.noteId)
        result.synced++
      } catch (error) {
        console.error(`❌ FrontendSync: Upload failed for note ${note.noteId}:`, error)
        result.errors.push(`Upload ${note.noteId}: ${error.message}`)
      }
    }

    // Execute downloads with batching
    for (const note of plan.download) {
      try {
        await this.saveNoteLocally(note)
        result.synced++
      } catch (error) {
        console.error(`❌ FrontendSync: Download failed for note ${note.noteId}:`, error)
        result.errors.push(`Download ${note.noteId}: ${error.message}`)
      }
    }

    return result
  }

  /**
   * Save note to local cache
   */
  async saveNoteLocally(note) {
    await this.saveNoteToCacheDB(note)
  }

  /**
   * Delete note from local cache
   */
  async deleteNoteLocally(noteId) {
    await this.deleteNoteFromCacheDB(noteId)
  }

  /**
   * Save note directly to IndexedDB
   */
  async saveNoteToCacheDB(note) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readwrite')
      const store = transaction.objectStore('notes')
      
      const noteToCache = {
        ...note,
        locallyModified: new Date(),
        needsSync: note.needsSync ?? false
      }
      
      return new Promise((resolve, reject) => {
        const request = store.put(noteToCache)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      if (error.name === 'NotFoundError' && error.message.includes('object stores was not found')) {
        console.log('🔄 FrontendSync: Object store missing in saveNoteToCacheDB, clearing cache...')
        await this.clearCache()
        // Don't throw, just skip caching
        return
      }
      console.error('❌ FrontendSync: Failed to save note to cache:', error)
      throw error
    }
  }

  /**
   * Delete note directly from IndexedDB
   */
  async deleteNoteFromCacheDB(noteId) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readwrite')
      const store = transaction.objectStore('notes')
      
      return new Promise((resolve, reject) => {
        const request = store.delete(noteId)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ FrontendSync: Failed to delete note from cache:', error)
      throw error
    }
  }

  /**
   * Load notes in background for cache refresh
   */
  async loadNotesInBackground() {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping background note loading')
        return
      }

      console.log('🔄 FrontendSync: Refreshing cache in background...')
      const result = await googleDriveAPI.loadNotes({ pageSize: 50 })
      await this.cacheNotes(result.notes)
      console.log(`✅ FrontendSync: Background cache refresh complete (${result.notes.length} notes)`)
    } catch (error) {
      console.warn('⚠️ FrontendSync: Background refresh failed:', error)
    }
  }

  /**
   * Update a single cached note
   */
  async updateCachedNote(note) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readwrite')
      const store = transaction.objectStore('notes')
      
      // Get existing note to preserve other fields
      const existingNote = await new Promise((resolve, reject) => {
        const request = store.get(note.noteId)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      
      if (existingNote) {
        // Merge with existing note, preserving all fields
        const updatedNote = { ...existingNote, ...note }
        await new Promise((resolve, reject) => {
          const request = store.put(updatedNote)
          request.onsuccess = () => resolve()
          request.onerror = () => reject(request.error)
        })
        console.log(`✅ FrontendSync: Updated cached note ${note.noteId} with cloudFileId`)
      } else {
        // Note doesn't exist in cache, add it
        await new Promise((resolve, reject) => {
          const request = store.add(note)
          request.onsuccess = () => resolve()
          request.onerror = () => reject(request.error)
        })
        console.log(`✅ FrontendSync: Added new note ${note.noteId} to cache`)
      }
    } catch (error) {
      console.error(`❌ FrontendSync: Failed to update cached note ${note.noteId}:`, error)
    }
  }

  /**
   * Cache notes in IndexedDB
   */
  async cacheNotes(notes) {
    for (const note of notes) {
      await this.saveNoteToCacheDB({
        ...note,
        cloudModified: note.lastModified,
        needsSync: false
      })
    }
  }

  async getCachedNoteById(noteId) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readonly')
      const store = transaction.objectStore('notes')
      
      return new Promise((resolve, reject) => {
        const request = store.get(noteId)
        request.onsuccess = () => resolve(request.result || null)
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      if (error.name === 'NotFoundError' && error.message.includes('object stores was not found')) {
        console.log('🔄 FrontendSync: Object store missing in getCachedNoteById, clearing cache...')
        await this.clearCache()
        return null
      }
      throw error
    }
  }

  /**
   * Load a specific note by ID using frontend-only sync
   */
  async loadNoteById(noteId, options = {}) {
    const { forceRefresh = false, minLastModified = false } = options

    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping note loading by ID')
      // Return cached version if available, otherwise null
      const cached = await this.getCachedNoteById(noteId)
      return cached || null
    }

    // Cache first unless forced
    if (!forceRefresh) {
      const cached = await this.getCachedNoteById(noteId)
      if (cached) {
        if (minLastModified && cached.lastModified && new Date(cached.lastModified) >= new Date(minLastModified)) {
          return cached
        }
        if (!minLastModified) {
          return cached
        }
        // Else cached is stale; continue to fetch
      }
    }
 
    const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
    const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
    if (!hasToken || !isInitialized) {
      throw new Error('Frontend sync not initialized')
    }
 
    // Resolve Drive file ID
    const fileId = await googleDriveAPI.getNoteFileId(noteId)
    if (!fileId) return null
 
    // Fetch note content directly from Drive
    const content = await googleDriveAPI.apiRequest(`${googleDriveAPI.API_BASE}/files/${fileId}`, { alt: 'media' })
    const noteData = (typeof content === 'string')
      ? JSON.parse(content)
      : content
 
    const result = {
      ...noteData,
      cloudFileId: fileId
    }
 
    // Cache it for subsequent loads
    try {
      await this.saveNoteToCacheDB(result)
    } catch (_) {}
 
    return result
  }

  // ------------------------------
  // Folder operations (frontend-only)
  // ------------------------------
  async moveNoteToFolderFrontend(noteId, targetFolderId, password = null) {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping note move')
        // Still move locally but don't attempt cloud sync
        const note = await this.getCachedNoteById(noteId)
        if (!note) {
          throw new Error(`Note ${noteId} not found in cache`)
        }

        // Determine if encryption/decryption is needed
        let updatedContent = note.content
        let isEncrypted = note.isEncrypted || false

        if (password) {
          // Get folder information to check if sensitive
          const targetFolder = await this.getFolderFromCacheDB(targetFolderId)
          const currentFolder = note.folderId ? await this.getFolderFromCacheDB(note.folderId) : null
          
          const isCurrentSensitive = currentFolder?.folderType === 'SENSITIVE'
          const isTargetSensitive = targetFolder?.folderType === 'SENSITIVE'

          if (!isCurrentSensitive && isTargetSensitive) {
            // Moving TO sensitive folder - ENCRYPT
            console.log('🔐 FrontendSync: Encrypting note content for sensitive folder')
            if (!window.localStorageService) {
              // Import the singleton instance (not the class)
              const localStorageService = (await import('./localStorageService')).default
              window.localStorageService = localStorageService
            }
            updatedContent = window.localStorageService.encryptContent(note.content, password)
            isEncrypted = true
            
          } else if (isCurrentSensitive && !isTargetSensitive) {
            // Moving FROM sensitive folder - DECRYPT
            console.log('🔓 FrontendSync: Decrypting note content from sensitive folder')
            if (!window.localStorageService) {
              // Import the singleton instance (not the class)
              const localStorageService = (await import('./localStorageService')).default
              window.localStorageService = localStorageService
            }
            updatedContent = window.localStorageService.decryptContent(note.content, password)
            isEncrypted = false
          }
          // If moving from sensitive to sensitive, no encryption change needed
        }

        const updated = {
          ...note,
          folderId: targetFolderId || null,
          content: updatedContent,
          isEncrypted: isEncrypted,
          lastModified: new Date().toISOString(),
          needsSync: true
        }

        // Persist locally only
        await this.saveNoteToCacheDB(updated)
        return { ...updated, localOnly: true }
      }

      // Load the note from cache
      const note = await this.getCachedNoteById(noteId)
      if (!note) {
        throw new Error(`Note ${noteId} not found in cache`)
      }

      // Determine if encryption/decryption is needed
      let updatedContent = note.content
      let isEncrypted = note.isEncrypted || false

      if (password) {
        // Get folder information to check if sensitive
        const targetFolder = await this.getFolderFromCacheDB(targetFolderId)
        const currentFolder = note.folderId ? await this.getFolderFromCacheDB(note.folderId) : null
        
        const isCurrentSensitive = currentFolder?.folderType === 'SENSITIVE'
        const isTargetSensitive = targetFolder?.folderType === 'SENSITIVE'

        if (!isCurrentSensitive && isTargetSensitive) {
          // Moving TO sensitive folder - ENCRYPT
          console.log('🔐 FrontendSync: Encrypting note content for sensitive folder')
          if (!window.localStorageService) {
            // Import the singleton instance (not the class)
            const localStorageService = (await import('./localStorageService')).default
            window.localStorageService = localStorageService
          }
          updatedContent = await window.localStorageService.encryptContent(note.content, password)
          isEncrypted = true
          
        } else if (isCurrentSensitive && !isTargetSensitive) {
          // Moving FROM sensitive folder - DECRYPT
          console.log('🔓 FrontendSync: Decrypting note content from sensitive folder')
          if (!window.localStorageService) {
            // Import the singleton instance (not the class)
            const localStorageService = (await import('./localStorageService')).default
            window.localStorageService = localStorageService
          }
          updatedContent = await window.localStorageService.decryptContent(note.content, password)
          isEncrypted = false
        }
        // If moving from sensitive to sensitive, no encryption change needed
      }

      const updated = {
        ...note,
        folderId: targetFolderId || null,
        content: updatedContent,
        isEncrypted: isEncrypted,
        lastModified: new Date().toISOString(),
        needsSync: true
      }

      // Persist locally first
      await this.saveNoteToCacheDB(updated)
      // Attempt cloud sync in background (do not block UI)
      const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
      const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
      if (navigator.onLine && hasToken && isInitialized) {
        googleDriveAPI
          .saveNote(updated)
          .then(() => this.markNoteSynced(updated.noteId))
          .catch((cloudError) => {
            console.warn('⚠️ FrontendSync: Cloud move failed, queuing for retry:', cloudError?.message || cloudError)
            this.offlineQueue.add('save', updated)
          })
      } else {
        this.offlineQueue.add('save', updated)
      }
      return updated
    } catch (error) {
      console.error('❌ FrontendSync: moveNoteToFolderFrontend failed:', error)
      throw error
    }
  }

  async moveNoteToRootFrontend(noteId) {
    return this.moveNoteToFolderFrontend(noteId, null)
  }

  async listFoldersFrontend() {
    await this.openCacheDB()
    try {
      console.log('🔍 FrontendSync: Starting listFoldersFrontend, checking GoogleDriveAPI state...')
      console.log('🔍 FrontendSync: GoogleDriveAPI available:', !!googleDriveAPI)
      console.log('🔍 FrontendSync: GoogleDriveAPI access token:', !!googleDriveAPI?.accessToken)
      console.log('🔍 FrontendSync: GoogleDriveAPI initialized:', !!googleDriveAPI?.initialized)
      console.log('🔍 FrontendSync: GoogleDriveAPI folder cache size:', googleDriveAPI?.folderCache?.size)
      
      // Check if CloudStorageContext is ready
      const cloudProvider = localStorage.getItem('cloudProvider')
      const cloudAccessToken = localStorage.getItem('cloudAccessToken')
      const cloudRefreshToken = localStorage.getItem('cloudRefreshToken')
      console.log('🔍 FrontendSync: Cloud storage state:', {
        cloudProvider,
        hasAccessToken: !!cloudAccessToken,
        hasRefreshToken: !!cloudRefreshToken
      })
      
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping folder listing')
        // Return cached folders only
        const cached = await this.getAllFoldersFromCacheDB()
        return cached || []
      }

      // Wait for CloudStorageContext to be ready (check if it has attempted initialization)
      if (!cloudProvider && !cloudAccessToken && !cloudRefreshToken) {
        console.log('⏳ FrontendSync: CloudStorageContext not ready, waiting for it to initialize...')
        await new Promise(resolve => setTimeout(resolve, 3000)) // Wait 3 seconds for CloudStorageContext
        
        // Check again after waiting
        const updatedCloudProvider = localStorage.getItem('cloudProvider')
        const updatedCloudAccessToken = localStorage.getItem('cloudAccessToken')
        const updatedCloudRefreshToken = localStorage.getItem('cloudRefreshToken')
        
        console.log('🔍 FrontendSync: After waiting for CloudStorageContext:', {
          cloudProvider: updatedCloudProvider,
          hasAccessToken: !!updatedCloudAccessToken,
          hasRefreshToken: !!updatedCloudRefreshToken
        })
      }
      
      // Wait for GoogleDriveAPI to be properly initialized with tokens
      if (!googleDriveAPI || !googleDriveAPI.accessToken) {
        console.log('⏳ FrontendSync: Waiting for GoogleDriveAPI to have access token...')
        
        // If we have a refresh token but no access token, wait a bit for CloudStorageContext to initialize
        if (cloudRefreshToken && !cloudAccessToken) {
          console.log('⏳ FrontendSync: Have refresh token but no access token, waiting for CloudStorageContext to initialize...')
          await new Promise(resolve => setTimeout(resolve, 2000)) // Wait 2 seconds for CloudStorageContext
        }
        
        try {
          await googleDriveAPI.waitForValidToken(10000) // Increased timeout to 10 seconds
          console.log('✅ FrontendSync: Finished waiting for valid token')
          console.log('🔍 FrontendSync: After wait - access token:', !!googleDriveAPI?.accessToken)
        } catch (error) {
          console.warn('⚠️ FrontendSync: Failed to wait for valid token, falling back to cached folders:', error)
          const cached = await this.getAllFoldersFromCacheDB()
          return cached || []
        }
      }
      
      // Final check - if still no token, fall back to cached folders
      if (!googleDriveAPI?.accessToken) {
        console.warn('⚠️ FrontendSync: Still no access token after wait, falling back to cached folders')
        const cached = await this.getAllFoldersFromCacheDB()
        return cached || []
      }
      
      // Ensure the API is initialized before proceeding
      if (!googleDriveAPI?.initialized || !googleDriveAPI.folderCache.get('FOLDERS')) {
        console.log('🔄 FrontendSync: Initializing Google Drive folder structure...')
        try {
          await googleDriveAPI.ensureAppFolderStructure()
          console.log('✅ FrontendSync: Google Drive folder structure initialized')
        } catch (error) {
          console.warn('⚠️ FrontendSync: Failed to initialize folder structure, falling back to cached folders:', error)
          const cached = await this.getAllFoldersFromCacheDB()
          return cached || []
        }
      }
      
      console.log('🔍 FrontendSync: About to call listFolderMetas...')
      const metas = await googleDriveAPI.listFolderMetas()

      // Deduplicate by default types and by ID
      const cached = await this.getAllFoldersFromCacheDB()
      const byId = new Map()
      for (const m of metas) {
        if (!byId.has(m.folderId)) byId.set(m.folderId, m)
      }
      let unique = Array.from(byId.values())

      const preferCachedIdForType = (type) => {
        const c = cached.find(f => f.folderType === type)
        return c?.folderId || null
      }

      const defaultTypes = ['PINNED', 'SENSITIVE']
      for (const type of defaultTypes) {
        const ofType = unique.filter(f => f.folderType === type)
        if (ofType.length > 1) {
          const cachedId = preferCachedIdForType(type)
          let keep
          if (cachedId) {
            keep = ofType.find(f => f.folderId === cachedId) || ofType[0]
          } else {
            keep = ofType.slice().sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))[0]
          }
          unique = unique.filter(f => f.folderType !== type || f.folderId === keep.folderId)
        }
      }

      // Update cache: save kept ones and delete removed duplicates
      const keptIds = new Set(unique.map(f => f.folderId))
      for (const f of unique) {
        try { await this.saveFolderToCacheDB(f) } catch (_) {}
      }
      // Remove duplicates from cache
      for (const cf of cached) {
        if (cf.folderType && defaultTypes.includes(cf.folderType) && !keptIds.has(cf.folderId)) {
          try { await this.deleteFolderFromCacheDB(cf.folderId) } catch (_) {}
        }
      }

      return unique
    } catch (e) {
      // Fallback to cached folders only
      const cached = await this.getAllFoldersFromCacheDB()
      return cached
    }
  }

  async getAllFoldersFromCacheDB() {
    const db = await this.openCacheDB()
    return new Promise((resolve) => {
      const tx = db.transaction(['notes'], 'readonly')
      const store = tx.objectStore('notes')
      const req = store.getAll()
      req.onsuccess = () => {
        const all = req.result || []
        const folders = all
          .filter(r => typeof r.noteId === 'string' && r.noteId.startsWith('__folder__:'))
          .map(rec => {
            const { noteId, ...folder } = rec
            return folder
          })
        resolve(folders)
      }
      req.onerror = () => resolve([])
    })
  }

  async createFolderFrontend(folderData) {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping folder creation')
      // Still create locally but don't attempt cloud sync
      const folder = {
        folderId: folderData.folderId || `folder-${Date.now()}-${Math.random().toString(36).substr(2,9)}`,
        name: folderData.name,
        folderType: folderData.folderType || 'CUSTOM',
        color: folderData.color || null,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      }
      await this.saveFolderToCacheDB(folder)
      return { ...folder, localOnly: true }
    }

    const folder = {
      folderId: folderData.folderId || `folder-${Date.now()}-${Math.random().toString(36).substr(2,9)}`,
      name: folderData.name,
      folderType: folderData.folderType || 'CUSTOM',
      color: folderData.color || null,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    }
    const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
    const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
    if (!navigator.onLine || !hasToken || !isInitialized) {
      // Queue for later
      this.offlineQueue.add('folder_create', folder)
      await this.saveFolderToCacheDB(folder)
      return folder
    }
    const res = await googleDriveAPI.saveFolderMeta(folder)
    if (res?.id) folder.cloudFileId = res.id
    await this.saveFolderToCacheDB(folder)
    return folder
  }

  async updateFolderFrontend(folderId, updateData) {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping folder update')
      // Still update locally but don't attempt cloud sync
      const existing = await this.getFolderFromCacheDB(folderId)
      const updated = { ...existing, ...updateData, lastModified: new Date().toISOString() }
      await this.saveFolderToCacheDB(updated)
      return { ...updated, localOnly: true }
    }

    const existing = await this.getFolderFromCacheDB(folderId)
    const updated = { ...existing, ...updateData, lastModified: new Date().toISOString() }
    const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
    const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
    if (!navigator.onLine || !hasToken || !isInitialized) {
      this.offlineQueue.add('folder_update', { folderId, updateData })
      await this.saveFolderToCacheDB(updated)
      return updated
    }
    const res = await googleDriveAPI.saveFolderMeta(updated)
    if (res?.id) updated.cloudFileId = res.id
    await this.saveFolderToCacheDB(updated)
    return updated
  }

  async deleteFolderFrontend(folderId) {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping folder deletion')
      // Still delete locally but don't attempt cloud sync
      await this.deleteFolderFromCacheDB(folderId)
      return { success: true, localOnly: true }
    }

    const hasToken = !!(googleDriveAPI && googleDriveAPI.accessToken)
    const isInitialized = !!(googleDriveAPI && googleDriveAPI.initialized)
    if (!navigator.onLine || !hasToken || !isInitialized) {
      this.offlineQueue.add('folder_delete', { folderId })
      await this.deleteFolderFromCacheDB(folderId)
      return true
    }
    await googleDriveAPI.deleteFolderMeta(folderId)
    await this.deleteFolderFromCacheDB(folderId)
    return true
  }

  // Cache helpers for folders in the same IndexedDB (CloudNotesCache)
  async saveFolderToCacheDB(folder) {
    const db = await this.openCacheDB()
    return new Promise((resolve, reject) => {
      let store
      if (!db.objectStoreNames.contains('folders')) {
        // Upgrade path not supported dynamically here; best-effort create a new db with folders store
        // Fall back to saving notes store metadata if needed
      }
      const tx = db.transaction(['notes'], 'readwrite')
      const storeNotes = tx.objectStore('notes')
      // Store folder metadata as a special note-like record with a reserved ID prefix to avoid schema migrations
      const record = { ...folder, noteId: `__folder__:${folder.folderId}` }
      const req = storeNotes.put(record)
      req.onsuccess = () => resolve(record)
      req.onerror = () => reject(req.error)
    })
  }

  async getFolderFromCacheDB(folderId) {
    const db = await this.openCacheDB()
    return new Promise((resolve) => {
      const tx = db.transaction(['notes'], 'readonly')
      const store = tx.objectStore('notes')
      const req = store.get(`__folder__:${folderId}`)
      req.onsuccess = () => {
        const rec = req.result
        if (rec) {
          const { noteId, ...folder } = rec
          resolve(folder)
        } else {
          resolve(null)
        }
      }
      req.onerror = () => resolve(null)
    })
  }

  async deleteFolderFromCacheDB(folderId) {
    const db = await this.openCacheDB()
    return new Promise((resolve) => {
      const tx = db.transaction(['notes'], 'readwrite')
      const store = tx.objectStore('notes')
      const req = store.delete(`__folder__:${folderId}`)
      req.onsuccess = () => resolve(true)
      req.onerror = () => resolve(false)
    })
  }

  /**
   * Get cached notes with automatic decryption for sensitive folders
   */
  async getCachedNotes(folderId = null) {
    try {
      const db = await this.openCacheDB()
      const transaction = db.transaction(['notes'], 'readonly')
      const store = transaction.objectStore('notes')
      
      return new Promise(async (resolve, reject) => {
        try {
        const request = store.getAll()
          request.onsuccess = async () => {
            let notes = request.result || []
            
            // Exclude folder metadata records stored with reserved IDs (preserve original logic)
            notes = notes.filter(rec => typeof rec.noteId === 'string' && !rec.noteId.startsWith('__folder__:'))
            
            // Sort by lastModified
            notes = notes.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))

            // 🔓 DECRYPT SENSITIVE NOTES FOR DISPLAY (this also filters out unauthenticated sensitive notes)
            notes = await this.decryptSensitiveNotesForDisplay(notes, folderId)
            
            // Apply folder filtering AFTER decryption/filtering
            if (folderId !== null && folderId !== undefined) {
              if (folderId === 'all') {
                // For "All Notes" - sensitive notes are already filtered out by decryptSensitiveNotesForDisplay
                // Just ensure we have all non-sensitive notes or authenticated sensitive notes
                console.log('📋 FrontendSync: Loading All Notes (excluding unauthenticated sensitive notes)')
              } else if (folderId === 'root') {
                // Notes that don't belong to any folder
                notes = notes.filter(note => !note.folderId)
                console.log('📋 FrontendSync: Loading root notes (no folder)')
              } else {
                // Filter by specific folder ID
                notes = notes.filter(note => note.folderId === folderId)
                console.log('📋 FrontendSync: Loading notes for folder:', folderId)
              }
            }
            
            console.log(`📦 FrontendSync: Retrieved ${notes.length} cached notes${folderId ? ` for folder context: ${folderId}` : ''}`)
            resolve(notes)
        }
        request.onerror = () => reject(request.error)
        } catch (error) {
          console.error('❌ FrontendSync: Error getting cached notes:', error)
          reject(error)
        }
      })
    } catch (error) {
      // Preserve original error handling logic
      if (error.name === 'VersionError') {
        console.log('🔄 FrontendSync: Version conflict in getCachedNotes, clearing cache...')
        await this.clearCache()
        return [] // Return empty array, fresh data will be loaded
      }
      if (error.name === 'NotFoundError' && error.message.includes('object stores was not found')) {
        console.log('🔄 FrontendSync: Object store missing in getCachedNotes, clearing cache...')
        await this.clearCache()
        return [] // Return empty array, fresh data will be loaded
      }
      throw error
    }
  }

  /**
   * Decrypt sensitive notes for display in the notes listing
   */
  async decryptSensitiveNotesForDisplay(notes, folderId = null) {
    try {
      // Check if we have access to sensitive session and folders
      let sessionInfo = null
      let folderInfo = null
      
      try {
        // Try to get current sensitive session from sessionStorage
        const sessionToken = sessionStorage.getItem('sensitiveToken')
        const sessionPassword = sessionStorage.getItem('sensitivePassword')
        const isAuthenticated = !!(sessionToken && sessionPassword)
        
        if (isAuthenticated) {
          sessionInfo = { sessionPassword, isAuthenticated: true }
        }
      } catch (error) {
        console.warn('🔍 FrontendSync: Could not access sensitive session info')
      }
      
      // Get folder info from global context instead of IndexedDB (more reliable)
      try {
        // Try to access folders from window context (set by FoldersContext)
        if (window.foldersContextData && window.foldersContextData.folders) {
          folderInfo = window.foldersContextData.folders
          console.log('🔍 FrontendSync: Using folders from global context:', folderInfo.length, 'folders')
        } else {
          // Fallback: use the correct method to get folders from IndexedDB (stored in notes store with __folder__: prefix)
          folderInfo = await this.getAllFoldersFromCacheDB()
          console.log('🔍 FrontendSync: Using folders from IndexedDB fallback:', folderInfo.length, 'folders')
        }
      } catch (error) {
        console.warn('🔍 FrontendSync: Could not access folder info, sensitive filtering may not work properly')
        folderInfo = []
      }
      
      // Process each note for potential decryption or filtering
      const processedNotes = []
      const isAllNotesView = folderId === 'all'
      
      for (const note of notes) {
        try {
          // Check if note is in a sensitive folder
          if (note.folderId && folderInfo && folderInfo.length > 0) {
            const noteFolder = folderInfo.find(f => f.folderId === note.folderId)
            
            if (noteFolder?.folderType === 'SENSITIVE') {
              console.log('🔒 FrontendSync: Found sensitive note:', note.noteId, 'isAllNotesView:', isAllNotesView, 'isAuthenticated:', sessionInfo?.isAuthenticated)
              
              // Check if note content appears to be encrypted
              const isEncrypted = note.isEncrypted || this.isContentEncrypted(note.content)
              
              if (isEncrypted && sessionInfo?.isAuthenticated && sessionInfo.sessionPassword && false) {
                // User is authenticated - but we don't auto-decrypt for list view
                // Sensitive notes should only be decrypted when explicitly accessed
                try {
                  // Import localStorageService for decryption
                  if (!window.localStorageService) {
                    const localStorageService = (await import('./localStorageService')).default
                    window.localStorageService = localStorageService
                  }
                  
                  const decryptedContent = window.localStorageService.decryptContent(
                    note.content, 
                    sessionInfo.sessionPassword
                  )
                  
                  console.log('🔓 FrontendSync: Decrypted note for display:', note.noteId)
                  
                  processedNotes.push({
                    ...note,
                    content: decryptedContent,
                    isDecryptedForDisplay: true // Mark as decrypted for display only
                  })
                  continue
                } catch (decryptError) {
                  console.warn('❌ FrontendSync: Failed to decrypt note for display:', note.noteId, decryptError)
                  
                  // In All Notes view, filter out failed decryption; in sensitive folder view, show error
                  if (isAllNotesView) {
                    console.log('🔒 FrontendSync: Filtering out failed decryption note from All Notes:', note.noteId)
                    continue // Skip this note entirely
                  } else {
                    processedNotes.push({
                      ...note,
                      content: '🔒 Failed to decrypt content',
                      isDecryptionFailed: true
                    })
                    continue
                  }
                }
              } else if (isEncrypted) {
                // Sensitive note - always show encrypted placeholder regardless of authentication
                if (isAllNotesView) {
                  // In All Notes view - FILTER OUT COMPLETELY
                  console.log('🔒 FrontendSync: Filtering out sensitive note from All Notes:', note.noteId)
                  continue // Skip this note entirely - don't add to processedNotes
                } else {
                  // In sensitive folder view - show placeholder
                  processedNotes.push({
                    ...note,
                    content: '🔒 This note is encrypted. Please authenticate to view its content.',
                    isEncryptedPlaceholder: true
                  })
                  continue
                }
              }
            }
          } else if (note.folderId && (!folderInfo || folderInfo.length === 0)) {
            console.warn('⚠️ FrontendSync: Note has folderId but no folder info available, cannot determine if sensitive:', note.noteId)
          }
          
          // Return note unchanged if not sensitive or not encrypted
          processedNotes.push(note)
        } catch (error) {
          console.warn('❌ FrontendSync: Error processing note for decryption:', note.noteId, error)
          processedNotes.push(note) // Keep original note on error
        }
      }
      
      console.log(`🔍 FrontendSync: Processed ${notes.length} notes, returning ${processedNotes.length} notes (${notes.length - processedNotes.length} filtered out)`)
      return processedNotes
    } catch (error) {
      console.error('❌ FrontendSync: Error decrypting sensitive notes:', error)
      // Return original notes if decryption fails
      return notes
    }
  }

  /**
   * Check if content appears to be encrypted (base64 encoded)
   */
  isContentEncrypted(content) {
    if (!content || typeof content !== 'string') return false
    
    try {
      // Check if content looks like encrypted data (AES-GCM produces base64-like strings)
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
      const looksLikeEncrypted = content.length > 50 && base64Regex.test(content)
      
      return looksLikeEncrypted
    } catch (error) {
      return false
    }
  }

  /**
   * Clear the entire IndexedDB cache (for version conflicts)
   */
  async clearCache() {
    try {
      console.log('🗑️ FrontendSync: Clearing IndexedDB cache due to version conflict...')
      
      if (this.cacheDB) {
        this.cacheDB.close()
        this.cacheDB = null
      }
      
      // Delete the entire database
      return new Promise((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase('CloudNotesCache')
        deleteRequest.onsuccess = () => {
          console.log('✅ FrontendSync: IndexedDB cache cleared successfully')
          resolve()
        }
        deleteRequest.onerror = () => {
          console.error('❌ FrontendSync: Failed to clear IndexedDB cache:', deleteRequest.error)
          reject(deleteRequest.error)
        }
        deleteRequest.onblocked = () => {
          console.log('⚠️ FrontendSync: IndexedDB delete blocked, will retry...')
          // Don't reject, just resolve and let it retry
          resolve()
        }
      })
    } catch (error) {
      console.error('❌ FrontendSync: Error clearing cache:', error)
      throw error
    }
  }

  /**
   * Mark note as synced
   */
  async markNoteSynced(noteId) {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping note sync marking')
      return
    }

    const db = await this.openCacheDB()
    const transaction = db.transaction(['notes'], 'readwrite')
    const store = transaction.objectStore('notes')
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(noteId)
      getRequest.onsuccess = () => {
        const note = getRequest.result
        if (note) {
          note.needsSync = false
          note.isOptimistic = false
          note.cloudModified = new Date()
          
          const putRequest = store.put(note)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          resolve() // Note not found, nothing to update
        }
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  /**
   * Check if there's a conflict between local and remote notes
   */
  async hasConflict(localNote, remoteNote) {
    // Simple conflict detection based on modification times and content
    const timeDiff = Math.abs(localNote.lastModified - remoteNote.lastModified)
    const hasTimeConflict = timeDiff < 60000 // Within 1 minute
    const hasContentConflict = localNote.content !== remoteNote.content
    
    return hasTimeConflict && hasContentConflict
  }

  /**
   * Setup network event listeners
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping network online handling')
        return
      }

      console.log('🌐 FrontendSync: Network restored, processing offline queue...')
      this.processOfflineQueue()
    })

    window.addEventListener('offline', () => {
      console.log('📱 FrontendSync: Network lost, enabling offline mode...')
    })
  }

  /**
   * Process offline queue when network is restored
   */
  async processOfflineQueue() {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping offline queue processing')
      return
    }

    const queuedOperations = this.offlineQueue.getAll()
    
    if (queuedOperations.length === 0) {
      console.log('✅ FrontendSync: No offline operations to process')
      return
    }

    console.log(`🔄 FrontendSync: Processing ${queuedOperations.length} offline operations...`)

    // Ensure we have valid tokens before processing
    const hasValidTokens = await this.ensureValidTokens()
    if (!hasValidTokens) {
      console.log('❌ FrontendSync: No valid tokens available, keeping operations queued')
      return
    }

    // Process each operation with retry logic
    for (const operation of queuedOperations) {
      await this.processOperationWithRetry(operation)
    }

    console.log('✅ FrontendSync: Offline queue processed')
  }

  /**
   * Ensure we have valid tokens for cloud operations
   */
  async ensureValidTokens() {
    try {
      // Check if we have valid Google Drive tokens
      const accessToken = localStorage.getItem('cloudAccessToken')
      const refreshToken = localStorage.getItem('cloudRefreshToken')
      
      if (!accessToken || !refreshToken) {
        console.log('🔄 FrontendSync: No cloud tokens, attempting refresh...')
        const refreshed = await this.refreshCloudTokens()
        return refreshed
      }
      
      // Check if tokens are expired
      const expiry = localStorage.getItem('googleDriveTokenExpiry')
      if (this.isTokenExpired(expiry)) {
        console.log('🔄 FrontendSync: Cloud tokens expired, refreshing...')
        const refreshed = await this.refreshCloudTokens()
        return refreshed
      }
      
      return true
      
    } catch (error) {
      console.error('❌ FrontendSync: Token validation failed:', error)
      return false
    }
  }

  /**
   * Refresh cloud tokens via cloudTokenService
   */
  async refreshCloudTokens() {
    try {
      // Import cloudTokenService dynamically to avoid circular dependencies
      const { default: cloudTokenService } = await import('./cloud/cloudTokenService.js')
      
      // Attempt to refresh the access token (includes cascading auth token refresh)
      const accessToken = await cloudTokenService.refreshAccessToken()
      
      if (accessToken) {
        console.log('✅ FrontendSync: Cloud tokens refreshed successfully')
        
        // Update GoogleDriveAPI with the new token
        if (googleDriveAPI && googleDriveAPI.updateAccessToken) {
          googleDriveAPI.updateAccessToken(accessToken)
        }
        
        return true
      }
      
      return false
      
    } catch (error) {
      console.error('❌ FrontendSync: Cloud token refresh failed:', error)
      return false
    }
  }

  /**
   * Check if a token is expired
   */
  isTokenExpired(expiryString) {
    if (!expiryString) return true
    
    try {
      const expiryTime = new Date(expiryString)
      const now = new Date()
      const bufferTime = 5 * 60 * 1000 // 5 minutes buffer
      
      return (expiryTime.getTime() - now.getTime()) < bufferTime
    } catch (error) {
      console.error('❌ FrontendSync: Error checking token expiry:', error)
      return true
    }
  }

  /**
   * Process a single operation with retry logic
   */
  async processOperationWithRetry(operation, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 FrontendSync: Processing operation ${operation.type} (attempt ${attempt}/${maxRetries})`)
        
        // Ensure tokens are valid before each attempt
        const hasValidTokens = await this.ensureValidTokens()
        if (!hasValidTokens) {
          throw new Error('No valid tokens available')
        }
        
        // Execute the operation
        await this.executeOperation(operation)
        
        // Remove from offline queue on success
        this.offlineQueue.remove(operation.id)
        console.log(`✅ FrontendSync: Operation ${operation.type} completed successfully`)
        return
        
      } catch (error) {
        console.error(`❌ FrontendSync: Operation ${operation.type} failed (attempt ${attempt}):`, error)
        
        if (attempt === maxRetries) {
          console.log(`❌ FrontendSync: Operation ${operation.type} failed after all retries, keeping in queue`)
          // Keep the operation in the offline queue for next network restoration
          return
        }
        
        // Wait before retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000) // Max 10 seconds
        console.log(`⏳ FrontendSync: Waiting ${delay}ms before retry...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  /**
   * Execute a single operation
   */
  async executeOperation(operation) {
    switch (operation.type) {
      case 'save':
        await googleDriveAPI.saveNote(operation.data)
        break
      case 'delete':
        await googleDriveAPI.deleteNote(operation.data.noteId, operation.data.cloudFileId, operation.data.noteContent)
        break
      case 'folder_create': {
        const res = await googleDriveAPI.saveFolderMeta(operation.data)
        if (res?.id) {
          const updated = { ...operation.data, cloudFileId: res.id }
          await this.saveFolderToCacheDB(updated)
        }
        break
      }
      case 'folder_update': {
        const existing = await this.getFolderFromCacheDB(operation.data.folderId)
        const merged = { ...existing, ...operation.data.updateData, lastModified: new Date().toISOString() }
        const res = await googleDriveAPI.saveFolderMeta(merged)
        if (res?.id) merged.cloudFileId = res.id
        await this.saveFolderToCacheDB(merged)
        break
      }
      case 'folder_delete': {
        await googleDriveAPI.deleteFolderMeta(operation.data.folderId)
        await this.deleteFolderFromCacheDB(operation.data.folderId)
        break
      }
      default:
        throw new Error(`Unknown operation type: ${operation.type}`)
    }
  }

  /**
   * Get sync statistics
   */
  getStats() {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 FrontendSync: Onboarding detected, skipping stats collection')
      return {
        ...this.stats,
        queueStatus: { status: 'onboarding', message: 'Stats collection disabled during onboarding' },
        isOnline: navigator.onLine,
        lastSyncTime: this.lastSyncTime,
        isInitialized: this.isInitialized,
        onboarding: true
      }
    }

    return {
      ...this.stats,
      queueStatus: googleDriveAPI.getSyncStatus(),
      isOnline: navigator.onLine,
      lastSyncTime: this.lastSyncTime,
      isInitialized: this.isInitialized
    }
  }

  /**
   * Simple test function to verify the service works
   */
  async testCacheAccess() {
    try {
      // Skip operations during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 FrontendSync: Onboarding detected, skipping cache access test')
        return { success: false, reason: 'onboarding' }
      }

      console.log('🧪 FrontendSync: Testing cache access...')
      const db = await this.openCacheDB()
      console.log('✅ FrontendSync: Cache database accessible')
      return true
    } catch (error) {
      console.error('❌ FrontendSync: Cache test failed:', error)
      return false
    }
  }
}

/**
 * Conflict Resolver
 * Handles merge conflicts between local and remote notes
 */
class ConflictResolver {
  async resolve(conflict) {
    const { local, remote } = conflict
    
    // Simple resolution strategy: newest wins
    // In a more advanced implementation, you could show a UI for manual resolution
    if (local.lastModified > remote.lastModified) {
      console.log(`🔀 FrontendSync: Conflict resolved - local newer for note ${local.noteId}`)
      return { action: 'upload', note: local }
    } else {
      console.log(`🔀 FrontendSync: Conflict resolved - remote newer for note ${remote.noteId}`)
      return { action: 'download', note: remote }
    }
  }
}

/**
 * Offline Queue
 * Manages operations that need to be synced when network is restored
 */
class OfflineQueue {
  constructor() {
    this.queue = this.loadFromStorage()
  }

  add(type, data) {
    const operation = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now()
    }
    
    this.queue.push(operation)
    this.saveToStorage()
    
    return operation.id
  }

  remove(id) {
    this.queue = this.queue.filter(op => op.id !== id)
    this.saveToStorage()
  }

  getAll() {
    return [...this.queue]
  }

  clear() {
    this.queue = []
    this.saveToStorage()
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem('frontendSyncQueue')
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  saveToStorage() {
    localStorage.setItem('frontendSyncQueue', JSON.stringify(this.queue))
  }
}

// Export singleton instance
export default new FrontendSyncService() 